import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {ScrollableModal} from '../Common/ScrollableModal';
import {Button} from '../Button/Button';
import {useDjangoContext} from '../../contexts/useAuthContext';

export type DeleteMutationResult = {
  ok: boolean | null;
  errors: (string | null)[] | null;
} | null;

interface PowerUserDeleteActionProps {
  /**
   * Human-readable name of the record being deleted, surfaced verbatim in the
   * confirmation dialog (e.g. "ECQ for John Smith").
   */
  recordLabel: string;
  /**
   * Whether the target record is currently in a pending state. The action is
   * only ever offered for pending records — completed/finalised records are out
   * of scope (ON-9069).
   */
  isPending: boolean;
  /** Fires the delete mutation and resolves with its `{ok, errors}` payload. */
  onConfirm: () => Promise<DeleteMutationResult>;
  /** Disables the buttons while the mutation is in flight. */
  loading?: boolean;
  /** Snackbar message shown on a successful delete. */
  successMessage: string;
  /** Fallback error message when the BE returns no specific error string. */
  defaultErrorMessage: string;
  /** Label on the trigger button (e.g. "Delete ECQ"). */
  buttonLabel: string;
  /** Optional callback fired after a successful delete (e.g. close the modal). */
  onDeleted?: () => void;
}

/**
 * Gated delete action + confirmation dialog for pending ECQ / RTQ records
 * (ON-9301).
 *
 * Renders nothing unless BOTH conditions hold:
 *   1. the current user is a power user (`djangoUser.isUserPowerUser`), and
 *   2. the target record is pending.
 *
 * Clicking the button opens a confirmation dialog that names the record and
 * warns the action cannot be undone; the mutation only fires on explicit
 * confirmation. Success and error feedback (including the BE's "no longer
 * pending" rejection) is surfaced via the existing snackbar pattern.
 */
export const PowerUserDeleteAction: React.FC<PowerUserDeleteActionProps> = ({
  recordLabel,
  isPending,
  onConfirm,
  loading = false,
  successMessage,
  defaultErrorMessage,
  buttonLabel,
  onDeleted,
}) => {
  const {djangoUser} = useDjangoContext();
  const {enqueueSnackbar} = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!djangoUser?.isUserPowerUser || !isPending) {
    return null;
  }

  const handleConfirm = async () => {
    try {
      const result = await onConfirm();
      if (result?.ok) {
        enqueueSnackbar(successMessage, {variant: 'success'});
        onDeleted?.();
      } else {
        // Surface the BE's specific reason (e.g. "no longer pending") when
        // present, otherwise fall back to a generic message.
        const beError = result?.errors?.find(
          (e): e is string => typeof e === 'string' && e.length > 0
        );
        enqueueSnackbar(beError ?? defaultErrorMessage, {
          variant: 'error',
          autoHideDuration: null,
        });
      }
    } catch (err) {
      enqueueSnackbar(defaultErrorMessage, {
        variant: 'error',
        autoHideDuration: null,
      });
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <ScrollableModal
        size="small"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <>
          <Typography mb={1}>
            Are you sure you want to delete <strong>{recordLabel}</strong>? This action
            cannot be undone.
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Button
              eventName={null}
              onClick={() => setConfirmOpen(false)}
              disabled={loading}
              variant="contained"
              color="primary"
            >
              Cancel
            </Button>
            <Button
              eventName={`Confirm delete ${buttonLabel}`}
              onClick={handleConfirm}
              disabled={loading}
              variant="contained"
              color="secondary"
            >
              Confirm delete
            </Button>
          </Box>
        </>
      </ScrollableModal>
      <Button
        eventName={buttonLabel}
        sx={{ml: 1}}
        onClick={() => setConfirmOpen(true)}
        disabled={loading}
        variant="contained"
        color="secondary"
      >
        {buttonLabel}
      </Button>
    </>
  );
};

export default PowerUserDeleteAction;
